import React, { useContext, useRef, useState, useEffect } from 'react';
import ContentEditable from 'react-contenteditable';
import styled from "styled-components";
import TextField from '@material-ui/core/TextField';

import LessonsContext from '@mirinae/lessons/modules/contexts/lessons';

import CircularProgress from '@material-ui/core/CircularProgress';
import httpAPI from '@mirinae/lessons/editor/modules/apis/http';
import { apiPaths, apiServer } from '@mirinae/shared/defines/paths';
import { SmallButton } from '@mirinae/lessons/editor/components/PropertySheets/UI';
import { Select } from '@mirinae/lessons/components/admin/LessonList';
import { wireNodeMap } from '@mirinae/lessons/editor/modules/libs/lessonLib';

//     setLesson: '/api/pattern/setlesson',
//     getPageOfPatterns: '/api/pattern/getpageof',

const PatternLink = styled.a`
    font-size: 10px;
    color: blue;
    align-self: flex-end;
    margin: 5px;
`;

const SearchBox = styled.div`
    display: flex;
    flex-direction: column;
    width: 600px;
  
    .MuiTextField-root {
        margin-top: 10px;
    }
`;

const SRPopupButttons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 20px;
`;

const Button = styled.div`
    width: fit-content;
    align-self: flex-end;
    margin-top: 10px;
`;

const Where = styled.div`
    span {
        font-size: 11px;
        margin-right: 10px;
    }
`;

const FoundLessons = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 12px;
    max-height: 600px;
    overflow-y: auto;
    margin: 8px 0;
`;

const Lesson = styled.div`
    display: flex;
    flex-direction: column;
    
    span {
        font-weight: bold;
        margin: 4px 0;
    }
`;

const Match = styled.div`
    margin-left: 20px;
`;

const String = styled.div`
    margin-left: 20px;
    width: 200px;
`;

const EditableReplacement = styled(ContentEditable)`
    font-size: 12px;
    margin-left: 20px;
    width: 200px;
`;

const Replace = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 0px;
    align-items: center;
`;

const Path = styled.div`
    color: #8a8a8a;
`;

const Checkbox = styled.input`
    margin: 0 6px 0 16px;
    accent-color: #d9d9d9;
`;

const Error = styled.div`
    color: red;
    font-weight: 600;
    text-align: center;
`;

const SwitchBar = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    font-size: 13px;
    color: #7d7d7d;
    input {
        margin: 0 20px 0 8px;
    }
`;

const GlobalSearchAndReplace = () => {
    const lessons = useContext(LessonsContext);
    const [whereToSearch, setWhereToSearch] = useState('Current list');
    const [haveIput, setHaveInput] = useState(false);
    const [searching, setSearching] = useState(false);
    const [exact, setExact] = useState(true);
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [foundLessons, setFoundLessons] = useState([]);
    const [error, setError] = useState('');
    const [numChecked, setNumChecked] = useState(0);
    const searchTextRef = useRef(null);
    const replaceRef = useRef(null);

    const performSearch = (e) => {
        setSearching(true);
        setError('');
        setFoundLessons([]);
        setNumChecked(0);

        const options = {
            data: { search: searchTextRef.current.value, maxCount: 100, exact, caseSensitive },
        };
        // note that this search depends on a mongodb text index being established.  For the moment, this has to be done by hand using mongosh or similar on
        // the fields and collections involved.
        // for this search, we add all lesson fields (eek!) to the index with this:
        //   db.getCollection('lesson').createIndex({"$**": "text"})
        // and use the $text find operator:
        //  db.getCollection('lesson').find({ $text: { $search: 'home' } })
        httpAPI(apiServer, apiPaths.globalSearch, options)
            .then(data => {
                setSearching(false);
                if (data.tooManyMatches) {
                    setError("Too many matches, make search string more specific");
                    return;
                }
                const searchText = searchTextRef.current ? searchTextRef.current.value.toLowerCase() : '????';
                data.lessons.forEach((lesson, i) => {
                    wireNodeMap(lesson.nodeMap);
                    // recurse through the lesson node structure, finding the nodes with the matched search-text
                    // and builds a rough description of their location in the lessons
                    const foundMatches = [];
                    const findMatchesInObj = (node, obj, path, name, thunk) => {
                        if (['background', 'typeDefPathName', 'name', 'type', 'componentName', ].includes(name))
                            return;
                        if (typeof obj === 'string') {
                            if (obj.toLowerCase().indexOf(searchText) >= 0) {
                                const edited = obj.replace(new RegExp(searchText, 'ig'), replaceRef.current ? replaceRef.current.value : searchText);
                                foundMatches.push({ path, obj, edited, apply: false, thunk });
                            }
                        } else if (Array.isArray(obj)) {
                            obj.forEach((e, i) => {
                                const fieldLabel = name === 'fields' && e.label;
                                findMatchesInObj(node, e, fieldLabel ? `${path}.${fieldLabel}` : path, name, [node, obj, i]);
                            });
                        } else if (typeof obj === 'object' && obj !== null && obj !== undefined) {
                            let objPath = path;
                            let objNode = node;
                            if (obj.nodeClass && obj.nodeID && lesson.nodeMap[obj.nodeID] === obj) {
                                objNode = obj;
                                if (obj.nodeClass === 'element')
                                    objPath = `${path}.${obj.componentName}`;
                                else if (obj.nodeClass === 'page')
                                    objPath = `${path}.${obj.componentName}`;
                                else if (obj.nodeClass === 'section')
                                    objPath = `${path}.${obj.title}`;
                            }
                            Object.getOwnPropertyNames(obj).forEach(name => {
                                if (name !== 'parent') {
                                    const prop = obj[name];
                                    findMatchesInObj(objNode, prop, objPath, name, [node, obj, name]);
                                }
                            })
                        }
                    };
                    // recurse node tree
                    const node = lesson.nodeMap[lesson.lessonNode.nodeID];
                    findMatchesInObj(node, node, ` (${node.nodeID})`, '');
                    if (foundMatches.length > 100) {
                        setError("Too many matches, make search string more specific");
                    }
                    lesson.matches = foundMatches.slice(0, 100);
                    lesson.index = i;
                });
                setFoundLessons(data.lessons);
            });
    };

    const apply = e => {
        lessons.setShowAlert(true, 'Applying edits, please wait...', 1000, true);
        lessons.update({ showGlobalSRDialog: false });
        const changedNodes = new Set();
        foundLessons.forEach(lesson => {
            lesson.matches.filter(m => m.apply).forEach(m => {
                const [node, obj, index] = m.thunk;
                obj[index] = m.edited;
                changedNodes.add(node);
            });
            delete lesson.matches;
            delete lesson.index;
        });
        lessons.updateDBNodeMap([...changedNodes]);
    };

    const replaceTextChanged = e => {
        const searchText = searchTextRef.current.value.toLowerCase();
        setFoundLessons(foundLessons.map(lesson => {
            lesson.matches.filter(m => !m.handEdited).forEach(m => {
                m.edited = m.obj.replace(new RegExp(searchText, 'ig'), replaceRef.current ? replaceRef.current.value : searchText);
            });
            return lesson;
        }));
    };

    const replaceTextEdited = (e, m) => {
        m.edited = e.target.textContent;
        m.handEdited = true;
    };

    const WhereToSearch = () => (
        <Where>
            <span>Where to search:</span>
            <Select selection={whereToSearch} options={['Current list', 'All lessons']} onChange={e => setWhereToSearch(e.target.value)} />
        </Where>
    );

    const Switches = () => (
        <SwitchBar>
            <span>Exact match</span><Checkbox type="checkbox" checked={exact} onChange={e => setExact(e.target.checked)} />
            <span>Case sensitive</span><Checkbox type="checkbox" checked={caseSensitive} onChange={e => setCaseSensitive(e.target.checked)} />
        </SwitchBar>
    );
    const LessonMatches = ({ lesson }) => {

        const setApply = (e, m) => {
            m.apply = e.target.checked;
            setNumChecked(numChecked + (m.apply ? 1 : -1));
            setFoundLessons([...foundLessons]);
        };

        return ( lesson.matches.map((m, i) => (
            <Match key={i}>
                <Path>{m.path.substring(1)}</Path>
                <Replace>
                    <String>{m.obj}</String><span>→</span>
                    <EditableReplacement html={m.edited} spellCheck={false} onBlur={e => replaceTextEdited(e, m)} />
                    <Checkbox type="checkbox" checked={m.apply} onChange={e => setApply(e, m)} />
                </Replace>
            </Match>
        )));
    };

    return (
        <SearchBox>
            {/* <WhereToSearch /> */}
            <Switches />
            <TextField
                size="small" inputRef={searchTextRef} onChange={e => setHaveInput(e.target.value.trim() !== '')} label="Find"
                InputProps={{
                    endAdornment: (
                        <React.Fragment>
                            {searching ? <CircularProgress color="inherit" size={20}/> : null}
                        </React.Fragment>
                    ),
                }}
            />
            <TextField
                size="small" inputRef={replaceRef} label="Replace" onChange={replaceTextChanged}
            />
            <Button><SmallButton variant="outlined" defaultBtn onClick={performSearch} disabled={!haveIput}>Search</SmallButton></Button>
            { error && <Error>{error}</Error> }
            {/*<TextField size="small" inputRef={replRef} onChange={() => setHaveInput(true)} label="Replace" />*/}
            <FoundLessons>
                { foundLessons.map(lesson => lesson.matches.length > 0 && (
                    <Lesson key={lesson._id}>
                        <span>{lesson.title}({lesson._id.toString()})</span>
                        <LessonMatches lesson={lesson} />
                    </Lesson>
                ))}
            </FoundLessons>
            <Button><SmallButton variant="outlined" onClick={apply}>Apply {numChecked} edits</SmallButton></Button>
            <Button><SmallButton variant="outlined" onClick={() => lessons.update({ showGlobalSRDialog: false })}>Cancel</SmallButton></Button>
        </SearchBox>
    );
};

export default GlobalSearchAndReplace;

